"use client";

import * as React from "react";
import { Box } from "../../components/ui/box";
import { useSettledMeasure } from "../../hooks/use-layout-settle";
import { cn } from "../../lib/utils";
import type { AppShellIsland } from "./app-shell";

/**
 * The gutter the island column carries on its leading edge — the ONLY gap
 * before its card, so the island gutter stays the standard 8px. The island's
 * resize seam owns this lane (the strip spans it, the themed indicator
 * centers in it). The columns before the island pay no trailing gutter of
 * their own, and the screen edge after it belongs to the shell row.
 */
export const ISLAND_HANDLE_LANE = 8;

/**
 * How a docked column opens and closes. Linear on purpose: the travel is a
 * whole pane, and an eased curve front-loads so much of it that the slide
 * reads as a snap with a tail. At the `base` rung the pane visibly glides the
 * whole way and the page rides it as one block (see the content pin in
 * {@link useDockMotion}).
 */
export const PANE_TRANSITION =
  "transition-[width] duration-base ease-linear motion-reduce:transition-none";

/**
 * The root custom properties carrying the islands region's viewport insets,
 * in px — its distance from each edge of the viewport. The region is the page
 * card plus the docked island and none of the navigation: it starts at the
 * page card's left edge (a pane joined into the card counts as inside, the
 * rail and the shell gutters do not) and ends at the docked island's right
 * edge — the card's own when the island is closed. Top and bottom are the card's. So a
 * toast anchored bottom-left may overlap the module menu but never the rail,
 * and one anchored right or center opens over a docked island instead of
 * being pushed off it. Body-portaled chrome that should stay inside the
 * islands (the toast viewport's `app-shell-islands` region) reads them;
 * nothing in the shell's own tree needs to.
 */
export const APP_SHELL_ISLANDS_INSET_VARS = {
  bottom: "--app-shell-islands-inset-bottom",
  left: "--app-shell-islands-inset-left",
  right: "--app-shell-islands-inset-right",
  top: "--app-shell-islands-inset-top",
} as const;

/**
 * Publishes {@link APP_SHELL_ISLANDS_INSET_VARS} — top, left and bottom from
 * the page card, right from the islands row — and clears them on unmount.
 * Where no islands row is mounted (mobile) the card closes the region on the
 * right too. Re-measured whenever either element resizes: the card fills
 * whatever the rail, pane and islands leave, and the row fills whatever the
 * rail and pane leave, so every layout change (a pane slide, an island
 * docking, a viewport resize) resizes one of them — plus the window resize
 * event for a same-size reposition. A layout effect so the first frame after
 * mount already carries the values: a toast already on screen must not jump
 * a frame late.
 *
 * Called from the shell, not the card: layout effects run child-first, and
 * the row is the card's ANCESTOR, so an effect inside the card would read
 * the row's ref before React attaches it. The shell's effect runs after every
 * descendant ref is set. The tier flip is the one thing that reparents both
 * elements while the shell stays mounted, so it is the re-run key.
 */
export function usePublishIslandsInsets(
  cardRef: React.RefObject<HTMLElement | null>,
  islandsRowRef: React.RefObject<HTMLElement | null>,
  isMobile: boolean,
) {
  // While the pane's settle window is open the card resizes every frame, and
  // a root custom-property write per frame invalidates style document-wide.
  // The writes are held and published once when the window closes — the
  // consumers (a toast viewport) reposition once at the settled rect.
  const measure = React.useCallback(() => {
    const card = cardRef.current;
    if (!card) return;
    const row = islandsRowRef.current ?? card;
    const root = document.documentElement.style;
    const rect = card.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    root.setProperty(APP_SHELL_ISLANDS_INSET_VARS.top, `${rect.top}px`);
    root.setProperty(
      APP_SHELL_ISLANDS_INSET_VARS.right,
      `${Math.max(0, window.innerWidth - rowRect.right)}px`,
    );
    root.setProperty(
      APP_SHELL_ISLANDS_INSET_VARS.bottom,
      `${Math.max(0, window.innerHeight - rect.bottom)}px`,
    );
    root.setProperty(APP_SHELL_ISLANDS_INSET_VARS.left, `${rect.left}px`);
  }, [cardRef, islandsRowRef]);
  const measureWhenSettled = useSettledMeasure(measure);
  React.useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const row = islandsRowRef.current ?? card;
    measure();
    const observer = new ResizeObserver(measureWhenSettled);
    observer.observe(card);
    if (row !== card) observer.observe(row);
    window.addEventListener("resize", measureWhenSettled);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measureWhenSettled);
      const root = document.documentElement.style;
      for (const name of Object.values(APP_SHELL_ISLANDS_INSET_VARS)) {
        root.removeProperty(name);
      }
    };
    // `isMobile` is the remount key, not a value the measurement reads.
  }, [cardRef, islandsRowRef, isMobile, measure, measureWhenSettled]);
}

/**
 * The right-panel island docked beside the page. The outer clips and
 * animates the column open ↔ closed while the inner holds the presented
 * width, so a close reveals/withdraws from the right edge instead of
 * reflowing. The clip box's width `transitionend` closes the dock motion's settle window. A
 * closed island is `inert`: its panel stays mounted (state survives, and it
 * slides out with content intact) but nothing inside it can take focus — a
 * clipped-away action must never be a tab stop.
 */
export function AppShellDockedIsland({
  island,
  presentedWidth,
  resizing,
  topInset,
  minWidth,
  maxWidth,
  onTransitionEnd,
  onWidthChange,
  onResizingChange,
}: {
  island: AppShellIsland;
  /** The column's width while open, handle lane included. */
  presentedWidth: number;
  /** A resize drag is live: the width transition yields to it. */
  resizing: boolean;
  /** No site header above: the island pays the top gutter itself. */
  topInset: boolean;
  minWidth: number;
  maxWidth: number;
  onTransitionEnd: React.TransitionEventHandler<HTMLDivElement>;
  onWidthChange: (width: number) => void;
  onResizingChange: (resizing: boolean) => void;
}): React.ReactElement {
  return (
    <Box
      className={resizing ? undefined : PANE_TRANSITION}
      data-slot="app-shell-right-panel"
      direction="row"
      height="100%"
      inert={!island.open || undefined}
      justify="end"
      onTransitionEnd={onTransitionEnd}
      overflow="hidden"
      position="relative"
      shrink={false}
      width={island.open ? presentedWidth : 0}
      zIndex={10}
    >
      <Box
        className={cn(
          resizing ? undefined : PANE_TRANSITION,
          // The seam is ALWAYS mounted and merely hidden when the
          // island is closed: flipping `resize` on and off
          // restructures the Box's children (seams join them in a
          // fragment), which remounts the card and loses panel state
          // across every close. display:none removes
          // the handle's visual, its hit area and its tab stop
          // without touching the tree.
          !island.open && "[&_[data-slot=box-resize-seam]]:hidden",
        )}
        direction="column"
        height="100%"
        // The shell owns the island's drag seam: its strip spans the
        // gutter lane (the column's left padding), its themed
        // indicator centers in that gap, and it drives the shared
        // width axis. The Box measures ITSELF, so the streamed width
        // carries the lane — strip it before publishing. The clamp
        // bounds apply only while open: CSS `min-width` would force a
        // closed column open.
        maxWidth={
          island.open
            ? maxWidth + ISLAND_HANDLE_LANE
            : undefined
        }
        minWidth={
          island.open
            ? minWidth + ISLAND_HANDLE_LANE
            : undefined
        }
        onResize={({ width }) =>
          onWidthChange(width - ISLAND_HANDLE_LANE)
        }
        onResizeEnd={() => onResizingChange(false)}
        onResizeStart={() => onResizingChange(true)}
        paddingBottom="sm"
        // The gutter lane — `sm` IS ISLAND_HANDLE_LANE; the constant
        // exists so the width math can name the same 8px.
        paddingLeft="sm"
        paddingTop={topInset ? "sm" : undefined}
        resize="left"
        resizeCentered
        resizeLabel={island.resizeLabel ?? "Resize panel"}
        shrink={false}
        width={presentedWidth}
      >
        <Box
          border
          borderColor="ink"
          direction="column"
          grow
          minHeight={0}
          overflow="hidden"
          rounded="md"
          surface="background"
        >
          {island.content}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * The island's mobile presentation: a full-screen layer sliding in from the
 * right — no scrim, it covers the whole content area. Stays mounted
 * (pointer-events gated) so its state survives a close.
 */
export function AppShellMobileIslandLayer({
  island,
}: {
  island: AppShellIsland;
}): React.ReactElement {
  const presented = island.open;
  return (
    <Box
      aria-hidden={!presented || undefined}
      inert={!presented || undefined}
      className={cn(
        // The pane recipe on the transform axis — same travel, same
        // settle, so the full-screen layer and the docked column
        // read as one motion family.
        "transition-transform duration-slow ease-emphasized motion-reduce:transition-none",
        presented
          ? "translate-x-0"
          : "pointer-events-none translate-x-full",
      )}
      data-slot="app-shell-right-panel"
      direction="column"
      inset={0}
      position="absolute"
      surface="background"
      zIndex={30}
    >
      {island.content}
    </Box>
  );
}
